"use client";

/**
 * Digging in a record crate (Gordon 2026-11-02: "a flip book but vertically
 * flipping like vinyls between the artwork … doing it with users would feel
 * like a rolodex"). The records stand in a crate leaning back; the front one
 * shows its whole sleeve, the ones behind only their top edges. Scroll, swipe
 * or press ↑/↓ and the front record tips forward toward you, the next one comes
 * up. Tap the front record to play (when it can) or open it.
 *
 * Plain CSS 3D — no WebGL, light on phones. Only a window of ~12 records is in
 * the DOM, so thousands are fine. Reduced motion: no tipping, just a swap.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { ArrowLeft, ChevronDown, ChevronUp, ExternalLink, FolderOpen, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import { cn } from "@/lib/utils";

export interface CrateItem {
  id: string;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  href?: string;
  onPlay?: () => void;
  /** Open inside the drawer instead of following href (a playlist → its tracks). */
  onOpen?: () => void;
}

/** Outline buttons on the dark crate glass: white line and text, any theme. */
const FACADE_BTN = "h-9 w-9 border-white/80 bg-transparent text-white hover:bg-white/15 hover:text-white disabled:border-white/40 disabled:text-white/50";
const BEHIND = 9;  // records drawn behind the front one
const FLIPPED = 2; // records already flipped forward, still drawn while they fall

export function CrateView({ items, label, className, fill, back }: {
  items: CrateItem[]; label: string; className?: string;
  /** Fill the parent (the drawer) instead of a fixed height. */ fill?: boolean;
  /** Inside a release/playlist: the way back, next to Open (Gordon 2026-11-03: "you expect it beside Open"). */
  back?: { label: string; onBack: () => void };
}) {
  const t = useTranslations("crates");
  // One look: the crate, covers flat and facing you (Gordon 2026-11-03: "the covers don't
  // need to be skewed, they can be flat — remove the Wide and From-above buttons").
  const wide = false;
  const open = false;
  const box = true;
  const [index, setIndex] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  // Records as large as the area allows (square sleeves).
  const [sleeve, setSleeve] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    // The info + buttons float on frosted glass over the records, so the
    // covers can take the whole height (Gordon 2026-11-02: "the covers must be
    // bigger — buttons on top, frosted").
    const measure = () => setSleeve(Math.floor(Math.min(el.clientWidth * (box ? 0.95 : 0.9), el.clientHeight * (wide ? 1.12 : box ? 0.93 : 0.9))));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [wide, box, items.length]);
  const wheelAcc = useRef(0);
  const lastStep = useRef(0);
  // Swipe: one record per 30 px while the finger moves, then a flick keeps
  // flipping for a while — harder swipe, more records, slowing down (Gordon
  // 2026-11-02: "smoother, and adapt to how hard or soft I swipe").
  const drag = useRef<{ y: number; moved: boolean; lastY: number; lastT: number; v: number } | null>(null);
  const flick = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopFlick = () => { if (flick.current) { clearTimeout(flick.current); flick.current = null; } };
  useEffect(() => stopFlick, []);

  useEffect(() => { setIndex(i => Math.min(i, Math.max(0, items.length - 1))); }, [items.length]);

  const step = useCallback((d: number) => {
    setIndex(i => Math.max(0, Math.min(items.length - 1, i + d)));
  }, [items.length]);

  // Wheel / trackpad: every ~40px flips one record; a fast spin flips up to 3
  // at once, so it follows the speed of the wheel.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      stopFlick();
      wheelAcc.current += e.deltaY;
      const now = Date.now();
      // One record at a time, at most every 130 ms, so each tip is seen whole (Gordon 2026-11-03: "smoother").
      if (Math.abs(wheelAcc.current) >= 50 && now - lastStep.current > 130) {
        step(wheelAcc.current > 0 ? 1 : -1);
        wheelAcc.current = 0;
        lastStep.current = now;
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [step]);

  const front = items[index];
  // Tapping the cover does what Open does (Gordon 2026-11-02); Space plays.
  const router = useRouter();
  const openItem = (item: CrateItem) => {
    if (item.onOpen) item.onOpen();
    else if (item.href) router.push(item.href);
    else item.onPlay?.();
  };

  if (!items.length) return <p className="py-16 text-center text-muted-foreground">{t("empty")}</p>;

  return (
    <div className={cn("flex w-full flex-col items-center gap-3", fill ? "h-full" : "h-[72vh] min-h-[420px]", className)}>
      <div
        ref={boxRef}
        tabIndex={0}
        role="group"
        aria-roledescription={t("crate")}
        aria-label={label}
        onKeyDown={e => {
          if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); step(1); }
          if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
          if (e.key === "Home") { e.preventDefault(); setIndex(0); }
          if (e.key === "End") { e.preventDefault(); setIndex(items.length - 1); }
          if (e.key === "Enter" && front) { e.preventDefault(); openItem(front); }
          if (e.key === " " && front) { e.preventDefault(); if (front.onPlay) front.onPlay(); else openItem(front); }
        }}
        // Swipe like flipping records: pull down = the front one tips toward you,
        // next record; push up = back (Gordon 2026-11-02: it moved the opposite way).
        onPointerDown={e => { stopFlick(); drag.current = { y: e.clientY, moved: false, lastY: e.clientY, lastT: performance.now(), v: 0 }; }}
        onPointerMove={e => {
          const d = drag.current;
          if (!d) return;
          const now = performance.now(), dt = Math.max(1, now - d.lastT);
          d.v = 0.7 * ((e.clientY - d.lastY) / dt) + 0.3 * d.v; // px per ms, smoothed
          d.lastY = e.clientY; d.lastT = now;
          const dy = e.clientY - d.y;
          if (Math.abs(dy) > 40) { step(dy > 0 ? 1 : -1); d.y = e.clientY; d.moved = true; }
        }}
        onPointerUp={() => {
          const d = drag.current;
          // A flick: the faster the finger, the more records keep flipping, each a little slower.
          if (d && d.moved && performance.now() - d.lastT < 80 && Math.abs(d.v) > 0.4) {
            const dir = d.v > 0 ? 1 : -1;
            let left = Math.min(20, Math.round((Math.abs(d.v) - 0.25) * 6));
            let wait = 90;
            const tick = () => {
              if (left-- <= 0) { flick.current = null; return; }
              step(dir);
              wait *= 1.18;
              flick.current = setTimeout(tick, wait);
            };
            tick();
          }
          setTimeout(() => { drag.current = null; }, 0);
        }}
        onPointerCancel={() => { drag.current = null; }}
        className="relative w-full min-h-0 flex-1 touch-none select-none outline-none focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
        style={wide ? { perspective: "1400px", perspectiveOrigin: "50% -60%" } : open ? { perspective: "1200px", perspectiveOrigin: "50% -70%" } : { perspective: "900px", perspectiveOrigin: "50% -35%" }}
      >
        {items.slice(Math.max(0, index - FLIPPED), index + BEHIND + 1).map((item, k) => {
          const i = Math.max(0, index - FLIPPED) + k;
          const o = i - index; // <0 flipped toward you, 0 front, >0 behind
          // Like a drawer pulled out, seen from above (Gordon 2026-11-02): each
          // record behind sits a little higher and further back, so its top edge
          // shows above the one in front. Flipped: tips toward you and fades.
          // Open drawer: seen from higher up, so each record behind shows more
          // of its top and the rows run deeper into the drawer. Wide: big
          // sleeves, tighter gaps — less of each record behind (Gordon 2026-11-02).
          const transform = wide
            // Wide (Gordon 2026-11-02): a little more from the front, less between the records behind.
            ? (o < 0 ? `translateY(12%) rotateX(-86deg)` : `translateY(${-o * 1.8}%) translateZ(${-o * 26}px) rotateX(-32deg)`)
            : open
            // Tighter rows (Gordon 2026-11-02: "smaller gaps between the covers").
            ? (o < 0 ? `translateY(10%) rotateX(-82deg)` : `translateY(${-o * 3.6}%) translateZ(${-o * 40}px) rotateX(-24deg)`)
            // Crate: a gentler lean so the covers keep their height (Gordon 2026-11-02: "looks squashed").
            // Crate: flat covers; the ones behind peek out above, smaller and darker.
            : (o < 0 ? `translateY(16%) translateZ(80px) rotateX(-72deg)` : `translateY(${-o * 5.5}%) translateZ(${-o * 42}px)`);
          return (
            <button
              key={item.id}
              type="button"
              tabIndex={-1}
              aria-hidden={o !== 0}
              onClick={() => { if (drag.current?.moved) return; if (o === 0) openItem(item); else setIndex(i); }}
              className={cn(
                "absolute inset-x-0 mx-auto aspect-square overflow-hidden rounded-[3px] bg-muted ring-1 ring-foreground/30 shadow-[0_-6px_18px_rgba(0,0,0,0.45)] motion-reduce:!transition-none",
                wide ? "bottom-0" : open ? "bottom-[1%]" : "bottom-[4%]",
              )}
              style={{ width: sleeve || undefined, height: sleeve || undefined, visibility: sleeve ? undefined : "hidden", transform, transformOrigin: "50% 100%",
                // Soft tip in and out: a long ease-out on the move, the fade a little quicker (Gordon 2026-11-03).
                transition: "transform 620ms cubic-bezier(0.22, 1, 0.36, 1), opacity 420ms ease-out, filter 620ms ease-out", willChange: "transform", zIndex: 100 - Math.abs(o) - (o < 0 ? 50 : 0), opacity: o < 0 ? 0 : 1, filter: o > 0 ? `brightness(${Math.max(0.45, 1 - o * (open ? 0.06 : 0.07))})` : undefined }}
            >
              {item.imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={item.imageUrl} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover" />
              ) : (
                <GenerativeArtistAvatar name={item.title} size={420} className="h-full w-full" />
              )}
              {/* The sleeve's top edge — keeps dark covers visible against a dark page. */}
              <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-foreground/50" />
            </button>
          );
        })}
        {/* Crate: the front is frosted glass, low down, carrying the record's
            info and buttons — the records stay big behind it. */}
        {box && front && (
          <div
            className="absolute inset-x-0 bottom-0 z-[200] mx-auto max-w-full px-2.5 pb-1.5 pt-2 sm:px-3 sm:pb-2 sm:pt-3"
            style={{ width: sleeve ? Math.round(sleeve * 1.04) : "100%" }}
            onPointerDown={e => e.stopPropagation()}
          >
            {/* Frosted glass behind the text and buttons; flat like the covers (Gordon 2026-11-03). */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-t-xl border border-white/25 bg-black/30 shadow-lg backdrop-blur-md backdrop-saturate-150"
            />
            <div className="relative">{caption()}</div>
          </div>
        )}
        {/* From above / Wide: a compact frosted card over the bottom of the front cover. */}
        {!box && front && (
          <div
            className="absolute inset-x-0 bottom-2 z-[200] mx-auto w-[min(100%,30rem)] rounded-xl border border-white/25 bg-black/30 px-2.5 py-1.5 shadow-lg backdrop-blur-md backdrop-saturate-150 sm:px-3 sm:py-2"
            onPointerDown={e => e.stopPropagation()}
          >
            {caption()}
          </div>
        )}
      </div>
    </div>
  );

  function caption() {
    if (!front) return null;
    return (
        // Light on the dark glass, whatever the theme (Gordon 2026-11-02: "text and buttons on the crate facade need to be light, white").
        // Compact on phones so more of the cover shows (Gordon 2026-11-02).
        <div className="space-y-1 text-center text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.45)] sm:space-y-2" aria-live="polite">
          <p className="truncate text-base font-bold leading-tight sm:text-lg" title={front.title}>{front.title}</p>
          {front.subtitle && <p className="truncate text-xs text-white/80 sm:text-sm">{front.subtitle}</p>}
          {/* Icons only, compact and centred (Gordon 2026-11-03: "symbols on the crate front, no text"). */}
          <div className="flex items-center justify-center gap-1.5">
            <Button type="button" variant="outline" size="icon" className={FACADE_BTN} onClick={() => step(-1)} disabled={index === 0} aria-label={t("previous")} title={t("previous")}><ChevronUp className="h-4 w-4" /></Button>
            {front.onPlay && <Button type="button" size="icon" className="h-9 w-9 bg-white text-black hover:bg-white/90 [text-shadow:none]" onClick={front.onPlay} aria-label={t("play")} title={t("play")}><Play className="h-4 w-4 translate-x-px" aria-hidden="true" /></Button>}
            {front.onOpen
              ? <Button type="button" variant="outline" size="icon" className={FACADE_BTN} onClick={front.onOpen} aria-label={t("open")} title={t("open")}><FolderOpen className="h-4 w-4" aria-hidden="true" /></Button>
              : front.href && <Button asChild variant="outline" size="icon" className={FACADE_BTN}><Link href={front.href} aria-label={t("open")} title={t("open")}><ExternalLink className="h-4 w-4" aria-hidden="true" /></Link></Button>}
            {back && <Button type="button" variant="outline" size="icon" className={FACADE_BTN} onClick={back.onBack} aria-label={back.label} title={back.label}><ArrowLeft className="h-4 w-4" aria-hidden="true" /></Button>}
            <Button type="button" variant="outline" size="icon" className={FACADE_BTN} onClick={() => step(1)} disabled={index >= items.length - 1} aria-label={t("next")} title={t("next")}><ChevronDown className="h-4 w-4" /></Button>
          </div>
          <p className="text-2xs text-white/75 tabular-nums sm:text-xs">{t("position", { n: index + 1, total: items.length })}<span className="hidden sm:inline"> · {t("hint")}</span></p>
        </div>
    );
  }
}
